import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import { bbqTerm } from '../utils/bbqTerms';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);
const API = (p) => `${config.API_BASE_URL}${p}`;
const bit = (v) => (v && v.data ? v.data[0] : Number(v) || 0);
const eur = (c) => (Number(c || 0) / 100).toFixed(2);
const cents = (s) => Math.round(parseFloat(String(s).replace(',', '.')) * 100) || 0;

// Fallback μέχρι να φορτώσουν τα δυναμικά πόστα της εταιρείας (8/8/2026)
const DEFAULT_STATIONS = [
  ['bbq', 'BBQ'], ['kitchen', 'Kitchen'], ['bar', 'Bar'],
  ['salads', 'Salads'], ['desserts', 'Desserts'], ['none', '—'],
];

const MenuManager = ({ embedded = false }) => {
  // Δυναμικά πόστα από το /stations-config — ο πελάτης ορίζει τα δικά του
  const [STATIONS, setStations] = useState(DEFAULT_STATIONS);
  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/stations-config`).then((r) => {
      const active = (r.data || []).filter((s) => s.active);
      if (active.length) setStations([...active.map((s) => [s.skey, s.name]), ['none', '—']]);
    }).catch(() => {});
  }, []);
  const [unavailable, setUnavailable] = useState(false);
  const [data, setData] = useState({ categories: [], items: [] });
  const [meats, setMeats] = useState([]);
  const [selCat, setSelCat] = useState(null);
  const [drafts, setDrafts] = useState({});      // itemId -> partial edits
  const [catDrafts, setCatDrafts] = useState({});
  const [newCat, setNewCat] = useState({ name: '', station: 'kitchen', vat_category: 13 });
  const [newCourse, setNewCourse] = useState('');
  const [editCourse, setEditCourse] = useState(null); // {id, name}
  const [newItem, setNewItem] = useState({ name: '', price: '', per_kg: 0, is_meat: 0, meat: '' });

  const load = useCallback(async () => {
    try {
      const [{ data: menu }, { data: meatRows }] = await Promise.all([
        axios.get(API('/ordering/menu')),
        axios.get(API('/meats')),
      ]);
      setData(menu);
      setMeats(Array.isArray(meatRows) ? meatRows : []);
      setSelCat((c) => c || menu.categories[0]?.id || null);
    } catch (e) {
      if (e?.response?.status === 403) { setUnavailable(true); return; }
      toast.error(e?.response?.data?.error || tr('Failed to load menu.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(
    () => data.items.filter((i) => i.category === selCat),
    [data.items, selCat]);
  const cat = data.categories.find((c) => c.id === selCat);

  const saveCat = async (c) => {
    const d = catDrafts[c.id]; if (!d) return;
    try {
      await axios.put(API(`/ordering/categories/${c.id}`), d);
      setCatDrafts((p) => { const n = { ...p }; delete n[c.id]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };

  const addCat = async () => {
    if (!newCat.name.trim()) return;
    try {
      await axios.post(API('/ordering/categories'), { ...newCat, sort: data.categories.length + 1 });
      setNewCat({ name: '', station: 'kitchen', vat_category: 13 });
      load();
    } catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };

  const deleteCat = async (c) => {
    if (!window.confirm(tr('Delete category "{{name}}";', { name: c.name }))) return;
    try { await axios.delete(API(`/ordering/categories/${c.id}`)); if (selCat === c.id) setSelCat(null); load(); }
    catch (e) { toast.error(e?.response?.data?.error || tr('Category has items.')); }
  };

  const patchItem = async (id, body) => {
    try {
      await axios.put(API(`/ordering/items/${id}`), body);
      setDrafts((p) => { const n = { ...p }; delete n[id]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };

  const addItem = async () => {
    if (!newItem.name.trim() || !selCat) return;
    try {
      await axios.post(API('/ordering/items'), {
        category: selCat, name: newItem.name,
        price_cents: cents(newItem.price), per_kg: newItem.per_kg ? 1 : 0,
        is_meat: newItem.is_meat ? 1 : 0,
        meat: newItem.is_meat && newItem.meat ? Number(newItem.meat) : null,
        vat_category: cat?.vat_category ?? 13,
        sort: items.length + 1,
      });
      setNewItem({ name: '', price: '', per_kg: 0, is_meat: 0, meat: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };

  const draftOf = (it) => drafts[it.id] || {};
  const setDraft = (id, patch) =>
    setDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  if (unavailable) return null;
  return (
    <div style={{ width: embedded ? '100%' : '96%', margin: embedded ? '26px 0 10px' : '10px auto' }}>
      {embedded && <h3>{tr('Ordering menu')}</h3>}
      <h3>{tr('Main tabs')}</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 18, alignItems: 'center' }}>
          {(data.courses || []).map((crs) => editCourse?.id === crs.id ? (
            <span key={crs.id} style={{ display: 'inline-flex', gap: 4 }}>
              <input autoFocus value={editCourse.name}
                onChange={(e) => setEditCourse({ ...editCourse, name: e.target.value })}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    await axios.put(API(`/ordering/courses/${crs.id}`), { name: editCourse.name });
                    setEditCourse(null); load();
                  }
                  if (e.key === 'Escape') setEditCourse(null);
                }} style={{ width: 110 }} />
            </span>
          ) : (
            <span key={crs.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 6px 5px 12px',
              border: '1px solid #dcdcdc', borderRadius: 16, background: '#fafafa', fontSize: 13,
            }}>
              <b>{crs.name}</b>
              <button style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2 }}
                onClick={() => setEditCourse({ id: crs.id, name: crs.name })}>✎</button>
              <button style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: '#b02a37' }}
                onClick={async () => {
                  if (!window.confirm(tr('Delete category "{{name}}";', { name: crs.name }))) return;
                  try { await axios.delete(API(`/ordering/courses/${crs.id}`)); load(); }
                  catch (e) { toast.error(tr('Course has categories.')); }
                }}>🗑</button>
            </span>
          ))}
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <input placeholder={tr('New tab')} value={newCourse}
              onChange={(e) => setNewCourse(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newCourse.trim()) {
                  await axios.post(API('/ordering/courses'), { name: newCourse });
                  setNewCourse(''); load();
                }
              }} style={{ width: 100 }} />
          </span>
        </div>


    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {/* Categories pane */}
      <div style={{ flex: '0 0 330px', minWidth: 300 }}>
        <h3>{tr('Menu categories')}</h3>
        <p style={{ fontSize: 11.5, opacity: 0.6, margin: '0 0 8px' }}>
          {tr('Per category: Station · Main tab (Starters/Mains/…) · VAT. Click the folder to select it.')}
        </p>
        {data.categories.map((c) => {
          const d = catDrafts[c.id] || {};
          const sel = c.id === selCat;
          return (
            <div key={c.id}
              onClick={() => setSelCat(c.id)}
              style={{
                border: `1px solid ${sel ? '#198754' : '#ccc'}`, borderRadius: 10,
                padding: '8px 10px', marginBottom: 8, cursor: 'pointer',
                background: sel ? 'rgba(25,135,84,0.07)' : 'white',
              }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ width: 24, textAlign: 'center', fontSize: 15, cursor: 'pointer' }}>
                  {sel ? '📂' : '📁'}
                </span>
                <input value={d.name ?? c.name}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setCatDrafts((p) => ({ ...p, [c.id]: { ...(p[c.id] || {}), name: e.target.value } }))}
                  title={tr('Category name (click to rename)')}
                  style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontWeight: 600 }} />
                <span style={{ fontSize: 11, opacity: 0.6 }}>{items && c.id === selCat ? items.length : ''}</span>
                <button className="rlToolbarBtn" style={{ padding: '2px 8px' }} title={tr('Delete')}
                  onClick={(e) => { e.stopPropagation(); deleteCat(c); }}>🗑</button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                <select style={{ flex: 1, minWidth: 88 }} value={d.station ?? c.station} title={tr('Station (screen that prepares it)')}
                  onChange={(e) => setCatDrafts((p) => ({ ...p, [c.id]: { ...(p[c.id] || {}), station: e.target.value } }))}>
                  {STATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <select style={{ flex: 1, minWidth: 96 }} value={d.default_course ?? (c.default_course || 2)} title={tr('Main tab in the ordering screen')}
                  onChange={(e) => setCatDrafts((p) => ({ ...p, [c.id]: { ...(p[c.id] || {}), default_course: Number(e.target.value) } }))}>
                  {(data.courses || []).map((crs) => (
                    <option key={crs.id} value={crs.id}>{crs.name}</option>
                  ))}
                </select>
                <select style={{ width: 92 }} value={d.vat_category ?? c.vat_category}
                  onChange={(e) => setCatDrafts((p) => ({ ...p, [c.id]: { ...(p[c.id] || {}), vat_category: Number(e.target.value) } }))}>
                  <option value={13}>{tr('VAT')} 13%</option>
                  <option value={24}>{tr('VAT')} 24%</option>
                </select>
                {catDrafts[c.id] && (
                  <button className="rlToolbarBtn rlToolbarBtnPrimary" style={{ padding: '3px 12px' }}
                    onClick={() => saveCat(c)}>✓ {tr('Save')}</button>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ borderTop: '1px dashed #bbb', paddingTop: 8, marginTop: 8, display: 'flex', gap: 6 }}>
          <input placeholder={tr('New category')} value={newCat.name}
            onChange={(e) => setNewCat((p) => ({ ...p, name: e.target.value }))}
            style={{ flex: 1, padding: 6 }} />
          <button className="rlToolbarBtn" onClick={addCat}>+</button>
        </div>
      </div>

      {/* Items pane */}
      <div style={{ flex: '1 1 520px', minWidth: 320 }}>
        <h3>{cat ? cat.name : tr('Items')}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>
                <th style={{ padding: 6 }}>{tr('Name')}</th>
                <th style={{ padding: 6 }}>{tr('Price')} (€)</th>
                <th style={{ padding: 6 }}>{tr('Per kg')}</th>
                <th style={{ padding: 6 }}>{bbqTerm('Meat (BBQ link)')}</th>
                <th style={{ padding: 6 }}>{tr('Hide')}</th>
                <th style={{ padding: 6 }}>{tr('Active')}</th>
                <th style={{ padding: 6 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const d = draftOf(it);
                const dirty = drafts[it.id] !== undefined;
                return (
                  <tr key={it.id} style={{ borderBottom: '1px solid #eee', opacity: bit(it.active) ? 1 : 0.45 }}>
                    <td style={{ padding: 6, minWidth: 180 }}>
                      <input value={d.name ?? it.name}
                        onChange={(e) => setDraft(it.id, { name: e.target.value })}
                        style={{ width: '95%', border: 'none', background: 'transparent' }} />
                      <div style={{ fontSize: 11, opacity: 0.55 }}>{it.description}</div>
                    </td>
                    <td style={{ padding: 6 }}>
                      <input value={d.price ?? eur(it.price_cents)}
                        onChange={(e) => setDraft(it.id, { price: e.target.value })}
                        style={{ width: 64, textAlign: 'right' }} />
                      {bit(d.per_kg ?? it.per_kg) ? <small> /kg</small> : null}
                    </td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!bit(d.per_kg ?? it.per_kg)}
                        onChange={(e) => setDraft(it.id, { per_kg: e.target.checked ? 1 : 0 })} />
                    </td>
                    <td style={{ padding: 6 }}>
                      <select value={d.meat ?? (it.meat || '')}
                        onChange={(e) => setDraft(it.id, {
                          meat: e.target.value || null, is_meat: e.target.value ? 1 : 0,
                        })}>
                        <option value="">{bbqTerm('— not a meat —')}</option>
                        {meats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!bit(it.out_of_stock)}
                        title={tr('Hide from ordering (out of stock)')}
                        onChange={(e) => patchItem(it.id, { out_of_stock: e.target.checked ? 1 : 0 })} />
                    </td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!bit(it.active)}
                        onChange={(e) => patchItem(it.id, { active: e.target.checked ? 1 : 0 })} />
                    </td>
                    <td style={{ padding: 6 }}>
                      {dirty && (
                        <button className="rlToolbarBtn" style={{ padding: '3px 10px' }}
                          onClick={() => {
                            const body = {};
                            if (d.name !== undefined) body.name = d.name;
                            if (d.price !== undefined) body.price_cents = cents(d.price);
                            if (d.per_kg !== undefined) body.per_kg = d.per_kg;
                            if (d.meat !== undefined) { body.meat = d.meat ? Number(d.meat) : null; body.is_meat = d.meat ? 1 : 0; }
                            patchItem(it.id, body);
                          }}>
                          {tr('Save')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: '#f6faf7' }}>
                <td style={{ padding: 6 }}>
                  <input placeholder={tr('New item name')} value={newItem.name}
                    onChange={(e) => setNewItem((p) => ({ ...p, name: e.target.value }))}
                    style={{ width: '95%' }} />
                </td>
                <td style={{ padding: 6 }}>
                  <input placeholder="0.00" value={newItem.price}
                    onChange={(e) => setNewItem((p) => ({ ...p, price: e.target.value }))}
                    style={{ width: 64, textAlign: 'right' }} />
                </td>
                <td style={{ padding: 6, textAlign: 'center' }}>
                  <input type="checkbox" checked={!!newItem.per_kg}
                    onChange={(e) => setNewItem((p) => ({ ...p, per_kg: e.target.checked ? 1 : 0 }))} />
                </td>
                <td style={{ padding: 6 }}>
                  <select value={newItem.meat}
                    onChange={(e) => setNewItem((p) => ({ ...p, meat: e.target.value, is_meat: e.target.value ? 1 : 0 }))}>
                    <option value="">{bbqTerm('— not a meat —')}</option>
                    {meats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </td>
                <td colSpan={2}></td>
                <td style={{ padding: 6 }}>
                  <button className="rlToolbarBtn rlToolbarBtnPrimary" onClick={addItem}>{tr('Add')}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, opacity: 0.6, marginTop: 10 }}>
          {bbqTerm('Items linked to a meat are sent to the BBQ board with timers & stickers. "Hide" removes an item from ordering without deleting it. Per-kg items are ordered in grams.')}
        </p>
      </div>
    </div>
    </div>
  );
};

export default MenuManager;
